import { useEffect, useState } from 'react'
import Layout from '@/components/layout/layout'
import { Box, Flex, Grid, Heading } from '@chakra-ui/react'
import OrderCard from '@/components/cards/order-card'
import ModalActionsDashboard from '@/components/modal/modal-actions-dashboard'
import JwtPayload from '@/entities/api/jwt'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import Toast from '@/components/elements/toast'
import { fetchOrders } from '@/services/api/orders/orders'
import { sessionRefrestUpdate } from '@/store/features/state'
import { setOrderFn, setOrdersFn } from '@/store/features/orders'
import SpinnerLoading from '@/components/elements/spinner'
import Search from '@/components/elements/search'
import Filter from '@/components/elements/filter'
import OrdersEntity, { OrderStatus } from '@/entities/api/orders/orders'
import { orderStatusMap } from '@/utils/maps/order-status-map'
import { ordersQuantityMap } from '@/utils/maps/quantity-orders-map'

const Orders = ({ data }: { data: JwtPayload }) => {
	const [modalOpen, setModalOpen] = useState<Boolean>(false)
	const { orders, selectOrder } = useSelector(
		(state: RootState) => state.orders
	)
	const [ordersFilter, setOrdersFilter] = useState<OrdersEntity[] | []>(orders)
	const [filter, setFilter] = useState<OrderStatus>(OrderStatus.new)
	const [isLoading, setIsloading] = useState<boolean>(false)
	const token = useSelector((state: RootState) => state.auth.token)
	const [limit, setLimit] = useState<number>(100)
	const [search, setSearch] = useState<string>('')
	const dispatch = useDispatch<AppDispatch>()
	const ordersRefrest = useSelector(
		(state: RootState) => state.state.ordersRefrest
	)

	const getOrders = async () => {
		try {
			setIsloading(true)
			if (orders.length === 0 || limit >= 10) {
				const response = await fetchOrders(token ?? '', {
					limit: limit,
					offset: 0,
					status: '',
				})
				if (response?.status === 401) {
					dispatch(sessionRefrestUpdate())
					return
				}
				dispatch(setOrdersFn(response?.data ?? []))
			}
			setIsloading(false)
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo obtener los cupones' })
			setIsloading(false)
		}
	}

	useEffect(() => {
		getOrders()
	}, [limit,  ordersRefrest])

	useEffect(() => {
		setOrdersFilter(orders)
	}, [orders])

	useEffect(() => {
		if (selectOrder) setModalOpen(true)
	}, [selectOrder])

	useEffect(() => {
		if (!orders) return

		const term = search?.trim().toLowerCase() ?? ''

		const filtered = orders.filter((order) => {
			const matchesSearch =
				term.length === 0 ||
				(order.customer?.name ?? '').toLowerCase().includes(term) ||
				(order.customer?.lastname ?? '').toLowerCase().includes(term) ||
				(order.code ?? '').toLowerCase().includes(term) ||
				(order.products ?? []).some((p) =>
					(p.name ?? '').toLowerCase().includes(term)
				) ||
				(order._id ?? '').toLowerCase().includes(term) ||
				(order.payment_info?.transaction_id ?? '').toLowerCase().includes(term)

			const matchesStatus =
				!filter || filter === OrderStatus.new || order.status === filter

			return matchesSearch && matchesStatus
		})

		setOrdersFilter(filtered)
	}, [search, filter, orders])

	const filterOpen = (value: OrderStatus) => {
		setFilter(value)
		setSearch('')
	}

	const openOrder = (order: OrdersEntity) => {
		dispatch(setOrderFn(order))
		setModalOpen(true)
	}

	return (
		<Layout data={data}>
			{isLoading ? (
				<SpinnerLoading color='black' size='xl' />
			) : (
				<>
					<Flex
						w='full'
						p='4'
						alignItems='center'
						flexDirection={{ base: 'column', lg: 'row' }}
						justifyContent={{ base: 'start', lg: 'space-between' }}
					>
						<Heading
							as='h2'
							color='black'
							fontSize={{ base: '2xl', sm: '3xl' }}
							textAlign={{ base: 'center', sm: 'start' }}
							whiteSpace='normal'
							wordBreak='break-word'
							fontWeight='800'
						>
							Pedidos
						</Heading>

						<Box
							w={{ base: 'full', lg: 'auto' }}
							flexDirection={{ base: 'column', sm: 'row' }}
							display='flex'
							alignItems='center'
							justifyContent={{ base: 'start', sm: 'center' }}
							p='4'
						>
							<Flex
								w={{ base: 'full', lg: 'auto' }}
								wrap='wrap'
								gap='4'
								justify={{ base: 'start', md: 'center' }}
							>
								<Box w={{ base: 'full', sm: 'auto' }}>
									<Search setSearch={setSearch} />
								</Box>
							</Flex>
							<Box w={{ base: 'full', sm: 'auto' }} mb={{ base: '4', sm: '0' }}>
								<Filter
									label='Tipo :'
									filters={orderStatusMap}
									setFilter={filterOpen}
								/>
							</Box>
							<Box w={{ base: 'full', sm: 'auto' }}>
								<Filter
									label='Cantidad :'
									filters={ordersQuantityMap}
									setFilter={(value) => setLimit(Number(value))}
								/>
							</Box>
						</Box>
					</Flex>
					<Box w='full' overflowX='auto'>
						<Grid
							minW='1400px'
							w='full'
							pt='2'
							pl={{ base: '4', xl: '6' }}
							pr={{ base: '4', xl: '6' }}
							pb={{ base: '5', xl: '10' }}
							gap='4'
							templateColumns='repeat(3, 1fr)'
						>
							<Flex
								bg='white'
								flexDirection='column'
								borderRadius='lg'
								boxShadow='md'
								p='4'
							>
								<Heading
									as='h3'
									fontSize='2xl'
									fontWeight='900'
									color='black'
									whiteSpace='normal'
									wordBreak='break-word'
									textAlign='center'
									mb='4'
								>
									Nuevos / Pendientes
								</Heading>
								{ordersFilter &&
									ordersFilter.length > 0 &&
									ordersFilter.map(
										(order, i) =>
											[OrderStatus.new, OrderStatus.pending].includes(
												order.status
											) && (
												<OrderCard
													key={i}
													click={() => openOrder(order)}
													order={order}
												/>
											)
									)}
							</Flex>
							<Flex
								bg='white'
								flexDirection='column'
								borderRadius='lg'
								boxShadow='md'
								p='4'
							>
								<Heading
									as='h3'
									fontSize='2xl'
									fontWeight='900'
									color='black'
									whiteSpace='normal'
									wordBreak='break-word'
									textAlign='center'
									mb='4'
								>
									En proceso / En camino
								</Heading>
								{ordersFilter &&
									ordersFilter.length > 0 &&
									ordersFilter.map(
										(order, i) =>
											[OrderStatus.processing, OrderStatus.on_the_way].includes(
												order.status
											) && (
												<OrderCard
													key={i}
													click={() => openOrder(order)}
													order={order}
												/>
											)
									)}
							</Flex>
							<Flex
								bg='white'
								flexDirection='column'
								borderRadius='lg'
								boxShadow='md'
								p='4'
							>
								<Heading
									as='h3'
									fontSize='2xl'
									fontWeight='900'
									color='black'
									whiteSpace='normal'
									wordBreak='break-word'
									textAlign='center'
									mb='4'
								>
									Entregados / Rechazados
								</Heading>
								{ordersFilter &&
									ordersFilter.length > 0 &&
									ordersFilter.map(
										(order, i) =>
											[OrderStatus.rejected, OrderStatus.delivered, OrderStatus.refunded].includes(
												order.status
											) && (
												<OrderCard
													key={i}
													click={() => openOrder(order)}
													order={order}
												/>
											)
									)}
							</Flex>
						</Grid>
					</Box>
				</>
			)}
			{modalOpen && (
				<ModalActionsDashboard
					open={modalOpen}
					close={() => {
						setModalOpen(false)
						dispatch(setOrderFn(null))
					}}
					titlePlus={`Orden #${selectOrder?.code ?? ''}`}
					action='open-order'
				/>
			)}
		</Layout>
	)
}

export default Orders
